import { SIX_TRUSTED_DEVICE } from '@/common/storage/keys'

export interface TrustedDevice {
    deviceGroupKey: string
    deviceKey: string
    deviceSecret: string
    owner: string
    createdAt: number
}

// Local defense-in-depth cap only — Cognito's own remembered-device lifetime is
// the actual server-side control and isn't exposed to this app.
export const TRUSTED_DEVICE_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000

function isExpired(device: TrustedDevice): boolean {
    return Date.now() - device.createdAt > TRUSTED_DEVICE_MAX_AGE_MS
}

export function getTrustedDevice(): TrustedDevice | null {
    try {
        const raw = localStorage.getItem(SIX_TRUSTED_DEVICE)
        return raw ? (JSON.parse(raw) as TrustedDevice) : null
    } catch {
        return null
    }
}

export function setTrustedDevice(device: Omit<TrustedDevice, 'createdAt'>): void {
    try {
        const stamped: TrustedDevice = { ...device, createdAt: Date.now() }
        localStorage.setItem(SIX_TRUSTED_DEVICE, JSON.stringify(stamped))
    } catch {
        // ignore — matches webauthn.ts's swallow-and-degrade behavior
    }
}

export function clearTrustedDevice(): void {
    try {
        localStorage.removeItem(SIX_TRUSTED_DEVICE)
    } catch {
        // ignore
    }
}

export function getValidTrustedDevice(owner: string): TrustedDevice | null {
    const device = getTrustedDevice()
    if (!device) return null
    if (device.owner !== owner || isExpired(device)) {
        clearTrustedDevice()
        return null
    }
    return device
}
